import { useEffect } from 'react';
import { useSocket } from '../context/SocketContext';

/**
 * Custom hook to sync orders with real-time updates
 * 
 * @param {Object} options - Hook options
 * @param {Array} options.orders - Current orders list
 * @param {Function} options.setOrders - Function to update orders list
 * @param {Object|null} options.selectedOrder - Currently selected order
 * @param {Function|null} options.setSelectedOrder - Function to update selected order
 * @param {Function} options.onOrderUpdate - Callback when an order is updated
 * @param {boolean} options.showNotification - Whether to show a notification on update
 * @param {Function} options.setSnackbar - Function to set snackbar state
 * @returns {Object} The socket instance
 */
const useOrderSync = ({
  orders = [],
  setOrders,
  selectedOrder = null,
  setSelectedOrder = null,
  onOrderUpdate = null,
  showNotification = true,
  setSnackbar = null
}) => {
  const { socket } = useSocket();
  
  useEffect(() => {
    if (socket) {
      
      // Listen for order updates
      const handleOrderUpdate = (data) => {
        
        // Update the order in the list if it exists
        if (setOrders) {
          setOrders(prev => {
            const updated = prev.map(order => 
              order.id === data.id ? { ...order, ...data.order } : order
            );
            return updated;
          });
        }
        
        // If we're currently viewing the details of this order, update it
        if (selectedOrder && selectedOrder.id === data.id && setSelectedOrder) {
          setSelectedOrder(prev => ({ ...prev, ...data.order }));
          
          // Show a notification if requested
          if (showNotification && setSnackbar) {
            setSnackbar({
              open: true,
              message: 'This order was just updated by another user',
              severity: 'info'
            });
          }
        }
        
        // Call additional update callback if provided
        if (onOrderUpdate) {
          onOrderUpdate(data.order);
        }
      };
      
      // Listen for order creation
      const handleOrderCreated = (data) => {
        
        // Add the new order to the list
        if (setOrders) {
          setOrders(prev => [data.order, ...prev]);
          
          // Show a notification if requested
          if (showNotification && setSnackbar) {
            setSnackbar({
              open: true,
              message: 'A new order was just created',
              severity: 'info'
            });
          }
        }
      };
      
      // Listen for order deletion
      const handleOrderDeleted = (data) => {
        
        // Remove the order from the list
        if (setOrders) {
          setOrders(prev => prev.filter(order => order.id !== data.id));
          
          // If we're currently viewing the details of this order, close it
          if (selectedOrder && selectedOrder.id === data.id && setSelectedOrder) {
            setSelectedOrder(null);
          }
          
          // Show a notification if requested
          if (showNotification && setSnackbar) {
            setSnackbar({
              open: true,
              message: 'An order was deleted by another user',
              severity: 'info'
            });
          }
        }
      };
      
      socket.on('order_updated', handleOrderUpdate);
      socket.on('order_created', handleOrderCreated);
      socket.on('order_deleted', handleOrderDeleted);
      
      // Clean up event listeners
      return () => {
        socket.off('order_updated', handleOrderUpdate);
        socket.off('order_created', handleOrderCreated);
        socket.off('order_deleted', handleOrderDeleted);
      };
    }
  }, [socket, orders, setOrders, selectedOrder, setSelectedOrder, onOrderUpdate, showNotification, setSnackbar]);
  
  return { socket };
};

export default useOrderSync; 